import type { GeoJsonDataType } from './types';
import { DataPrimitive, TypedValue, DataType, IDataPoint } from './DataPrimitive';
import { TypeSafeValue } from './TypeSafeValue';
import { isNumber } from './utils/types';

/**
 * Base DataPoint class and associated DataPoint selectors
 * @implements {DataPrimitive}
 * @implements {IDataPoint}
 */
export class DataPoint<T extends DataType = DataType> implements DataPrimitive<T>, IDataPoint<T> {
    private value: TypeSafeValue<T>;

    public field: string;

    /**
     * Optional prop indicating if this DataPoint originated from a search result.
     */
    public isSearchResult?: boolean;

    static count = 0; // NOSONAR - incremented at runtime

    static isDataPoint(o: any): o is DataPoint {
        return o instanceof DataPoint;
    }

    static fromRaw(value): DataPoint {
        return new DataPoint('', TypeSafeValue.fromRaw(value));
    }

    /**
     * @param {string} field data field
     * @param {object} value data value + it's type (number, string, time, color, geojson)
     * @param {object} isSearchResult optional prop for search result tagging
     */
    public constructor(field: string, value: TypedValue<T>, isSearchResult?: boolean) {
        this.field = field;
        this.setValue(value);
        if (isSearchResult) {
            Object.defineProperty(this, 'isSearchResult', {
                value: isSearchResult,
                writable: false,
                configurable: true,
                enumerable: true,
            });
        }
        DataPoint.count += 1;
    }

    getValue(): TypeSafeValue<T> {
        return this.value;
    }

    /**
     * Sets the data point's value to a static TypedValue.
     * @param {TypedValue} v
     */
    setValue(v: TypedValue<T>): void {
        this.value = TypeSafeValue.from(v);
    }

    /**
     * Get only value of the data point.
     * @returns {string|number|GeoJsonDataType|null}
     */
    getRawValue(): string | number | GeoJsonDataType | null {
        return this.value.toRawValue();
    }

    /**
     * Get only the coerced value of the data point.
     * @returns {string|number|null}
     */
    getCoercedValue(): string | number | GeoJsonDataType | null {
        return this.value.toRawCoercedValue();
    }

    /**
     * Returns the data field of the point.
     * @public
     * @returns {DataPoint<'string'>}
     */
    getField(): DataPoint<'string'> {
        return DataPoint.fromRaw(this.field) as DataPoint<'string'>;
    }

    /**
     * Returns the data type of the point.
     * @public
     * @returns {string}
     */
    getType(): string {
        return this.value.type;
    }

    /**
     * Adds a number to the current data point value and returns a new DataPoint.
     * Numeric types and numeric strings are both supported.
     *
     * @public
     * @param {number} value amount to add to the current point
     * @returns {DataPoint<'number'>} DataPoint with the adjusted numeric value, or undefined for non-numeric input
     */
    plus(value: number): DataPoint<'number'> {
        const currentValue = this.getRawValue();
        if (!Number.isFinite(value) || !isNumber(currentValue)) {
            return undefined;
        }

        return new DataPoint(
            this.field,
            { type: 'number', value: +currentValue + value },
            this.isSearchResult
        );
    }

    /**
     * Subtracts a number from the current data point value and returns a new DataPoint.
     * Numeric types and numeric strings are both supported.
     *
     * @public
     * @param {number} value amount to subtract from the current point
     * @returns {DataPoint<'number'>} DataPoint with the adjusted numeric value, or undefined for non-numeric input
     */
    minus(value: number): DataPoint<'number'> {
        const currentValue = this.getRawValue();
        if (!Number.isFinite(value) || !isNumber(currentValue)) {
            return undefined;
        }

        return new DataPoint(
            this.field,
            { type: 'number', value: +currentValue - value },
            this.isSearchResult
        );
    }

    /**
     * Multiplies the current data point value by a number and returns a new DataPoint.
     * Numeric types and numeric strings are both supported.
     *
     * @public
     * @param {number} value amount to multiply the current point by
     * @returns {DataPoint<'number'>} DataPoint with the adjusted numeric value, or undefined for non-numeric input
     */
    multiply(value: number): DataPoint<'number'> {
        const currentValue = this.getRawValue();
        if (!Number.isFinite(value) || !isNumber(currentValue)) {
            return undefined;
        }

        return new DataPoint(
            this.field,
            { type: 'number', value: +currentValue * value },
            this.isSearchResult
        );
    }

    /**
     * Divides the current data point value by a number and returns a new DataPoint.
     * Numeric types and numeric strings are both supported.
     *
     * @public
     * @param {number} value amount to divide the current point by
     * @returns {DataPoint<'number'>} DataPoint with the adjusted numeric value, or undefined for non-numeric input
     */
    divide(value: number): DataPoint<'number'> {
        const currentValue = this.getRawValue();
        if (!Number.isFinite(value) || value === 0 || !isNumber(currentValue)) {
            return undefined;
        }

        return new DataPoint(
            this.field,
            { type: 'number', value: +currentValue / value },
            this.isSearchResult
        );
    }

    /**
     * Returns the absolute value of the current data point as a new DataPoint.
     * Numeric types and numeric strings are both supported.
     *
     * @public
     * @returns {DataPoint<'number'>} DataPoint with absolute value, or undefined for non-numeric input
     */
    abs(): DataPoint<'number'> {
        const currentValue = this.getRawValue();
        if (!isNumber(currentValue)) {
            return undefined;
        }

        return new DataPoint(
            this.field,
            { type: 'number', value: Math.abs(+currentValue) },
            this.isSearchResult
        );
    }

    /**
     * Rounds the current data point value to the nearest integer and returns a new DataPoint.
     * Numeric types and numeric strings are both supported.
     *
     * @public
     * @returns {DataPoint<'number'>} DataPoint with rounded value, or undefined for non-numeric input
     */
    round(): DataPoint<'number'> {
        const currentValue = this.getRawValue();
        if (!isNumber(currentValue)) {
            return undefined;
        }

        return new DataPoint(
            this.field,
            { type: 'number', value: Math.round(+currentValue) },
            this.isSearchResult
        );
    }

    /**
     * Floors the current data point value to the nearest lower integer and returns a new DataPoint.
     * Numeric types and numeric strings are both supported.
     *
     * @public
     * @returns {DataPoint<'number'>} DataPoint with floored value, or undefined for non-numeric input
     */
    floor(): DataPoint<'number'> {
        const currentValue = this.getRawValue();
        if (!isNumber(currentValue)) {
            return undefined;
        }

        return new DataPoint(
            this.field,
            { type: 'number', value: Math.floor(+currentValue) },
            this.isSearchResult
        );
    }

    /**
     * Ceils the current data point value to the nearest higher integer and returns a new DataPoint.
     * Numeric types and numeric strings are both supported.
     *
     * @public
     * @returns {DataPoint<'number'>} DataPoint with ceiled value, or undefined for non-numeric input
     */
    ceil(): DataPoint<'number'> {
        const currentValue = this.getRawValue();
        if (!isNumber(currentValue)) {
            return undefined;
        }

        return new DataPoint(
            this.field,
            { type: 'number', value: Math.ceil(+currentValue) },
            this.isSearchResult
        );
    }
}
